import React, { useEffect, useContext, useState } from "react";
import { Link } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import { AppSettings } from "../../config/app-settings.js";
import SidebarMinifyBtn from "./sidebar-minify-btn.jsx";
import { useLocation } from "react-router-dom";
import ModelMappingSelector from "../selector/model-mapping-selector.jsx";
import DashboardSelector from "../selector/dashboard-selector.jsx";
import EvacuationPortalSelector from "../selector/evacuation-portal-selector.jsx";
import EvacuationEventDetails from "../details/evacuation-event-details.jsx";

function SidebarUtils() {
  const location = useLocation();
  const [currentComponents, setCurrentComponents] = useState([]);

  // Component mapping for dynamic content
  const componentMap = {
    "model-mapping-selector": ModelMappingSelector,
    "dashboard-selector": DashboardSelector,
    "evacuation-portal-selector": EvacuationPortalSelector,
    "evacuation-event-details": EvacuationEventDetails,
  };

  // Route-based default configurations (using page identifiers, not full paths)
  const routeComponentConfig = {
    "evacuation-portal": [
      { component: "evacuation-event-details", props: {} },
      { component: "evacuation-portal-selector", props: {} },
    ],
    "model-mapping": [{ component: "model-mapping-selector", props: {} }],
    dashboard: [{ component: "dashboard-selector", props: {} }],
  };

  // Helper function to extract page identifier from pathname
  const getPageIdentifier = (pathname) => {
    // Remove leading/trailing slashes and split by '/'
    const segments = pathname
      .split("/")
      .filter((segment) => segment.length > 0);

    // Find the last meaningful segment (ignoring common suffixes like '-panels')
    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i];
      // Skip panel-related segments
      if (!segment.endsWith("-panels")) {
        return segment;
      }
    }

    return null;
  };

  // Update content when pathname changes
  useEffect(() => {
    const pathname = location.pathname;
    const pageIdentifier = getPageIdentifier(pathname);

    if (!pageIdentifier) {
      console.log("No page identifier found for pathname:", pathname);
      setCurrentComponents([]);
      return;
    }

    const configForRoute = routeComponentConfig[pageIdentifier];

    if (!configForRoute) {
      console.log("No sidebar configuration for page:", pageIdentifier);
      setCurrentComponents([]);
      return;
    }

    // Render components for this route
    const renderedComponents = configForRoute
      .map((content) => {
        if (!content || !content.component) return null;
        const Component = componentMap[content.component];
        if (!Component) {
          console.warn(
            `Component "${content.component}" not found in componentMap`
          );
          return null;
        }

        return {
          id: content.component,
          element: <Component key={content.component} {...content.props} />,
        };
      })
      .filter((c) => c !== null);

    setCurrentComponents(renderedComponents);

    // Cleanup function
    return () => {
      setCurrentComponents([]);
    };
  }, [location.pathname]);

  // Render content based on state
  const renderSidebarContent = () => {
    if (currentComponents.length === 0) {
      return (
        <h5 className="mb-3 text-gray-500 text-center">
          <b>No Controls Available</b>
          <span className="ms-2">
            <i
              className="fa fa-warning"
              data-bs-toggle="popover"
              data-bs-trigger="hover"
              data-bs-title="Controls Unavailable"
              data-bs-placement="bottom"
              data-bs-content="Controls for the current page are not available."
            ></i>
          </span>
        </h5>
      );
    }

    return (
      <div className="d-flex flex-column gap-1">
        {currentComponents.map((comp) => comp.element)}
      </div>
    );
  };

  return (
    <AppSettings.Consumer>
      {({
        toggleAppSidebarMinify,
        toggleAppSidebarMobile,
        appSidebarTransparent,
        appSidebarGrid,
        appSidebarLight,
      }) => (
        <React.Fragment>
          <div
            id="sidebar"
            className={
              "app-sidebar " +
              (appSidebarTransparent ? "app-sidebar-transparent" : "") +
              (appSidebarGrid ? "app-sidebar-grid" : "")
            }
            data-bs-theme={appSidebarLight ? "" : "dark"}
          >
            <PerfectScrollbar
              className="app-sidebar-content"
              options={{ suppressScrollX: true }}
            >
              <div className="d-flex align-items-center justify-content-center w-100 h-100">
                {/* Content */}
                <div className="row h-100 w-100">
                  <div className="col-lg-12 p-0 d-flex flex-column justify-content-center">
                    {/* <SidebarMinifyBtn /> */}
                    {renderSidebarContent()}
                  </div>
                </div>
              </div>
            </PerfectScrollbar>
          </div>
          <div
            className="app-sidebar-bg"
            data-bs-theme={appSidebarLight ? "" : "dark"}
          ></div>
          <div className="app-sidebar-mobile-backdrop">
            <Link
              to="/"
              onClick={toggleAppSidebarMobile}
              className="stretched-link"
            ></Link>
          </div>
        </React.Fragment>
      )}
    </AppSettings.Consumer>
  );
}

export default SidebarUtils;
